/**
 * How far a hand is from winning, for the standard shape (4 sets + 1 pair).
 *
 * Strategy code only: the rules themselves live in @kikik/mahjong-core.
 * -1 means the hand is already complete, 0 means ready (tenpai), 1 means one
 * useful tile away from ready, and so on.
 */

import { TILE_INFO, TILE_KINDS, countKinds, kindAtIndex } from "@kikik/mahjong-core";
import type { TileKind } from "@kikik/mahjong-core";

const HONOR_START_INDEX = 27;
const KIND_COUNT = TILE_KINDS.length;

function at(counts: readonly number[], index: number): number {
  return counts[index] ?? 0;
}

function canStartRunAtIndex(index: number): boolean {
  return index < HONOR_START_INDEX && index % 9 <= 6;
}

function canReachAtIndex(index: number, gap: number): boolean {
  return index < HONOR_START_INDEX && (index % 9) + gap <= 8;
}

/**
 * Classic block formula. A hand needs four sets and one pair:
 * every finished set is worth two steps, every partial set and the pair one.
 */
function score(sets: number, partials: number, pairs: number): number {
  const finished = Math.min(4, sets);
  const hasPair = pairs > 0 ? 1 : 0;
  // Extra pairs are partial sets waiting to become triplets.
  let blocks = partials + Math.max(0, pairs - 1);
  const capacity = Math.max(0, 4 - finished);
  if (blocks > capacity) blocks = capacity;
  return (4 - finished) * 2 - blocks - hasPair;
}

function search(
  counts: number[],
  index: number,
  sets: number,
  partials: number,
  pairs: number,
): number {
  while (index < KIND_COUNT && at(counts, index) === 0) index += 1;
  if (index >= KIND_COUNT) return score(sets, partials, pairs);

  let best = Number.POSITIVE_INFINITY;
  const blocksUsed = sets + partials + pairs;

  const take = (offset: number, amount: number): void => {
    counts[index + offset] = at(counts, index + offset) - amount;
  };
  const give = (offset: number, amount: number): void => {
    counts[index + offset] = at(counts, index + offset) + amount;
  };

  if (at(counts, index) >= 3) {
    take(0, 3);
    best = Math.min(best, search(counts, index, sets + 1, partials, pairs));
    give(0, 3);
  }

  if (
    canStartRunAtIndex(index) &&
    at(counts, index + 1) > 0 &&
    at(counts, index + 2) > 0
  ) {
    take(0, 1);
    take(1, 1);
    take(2, 1);
    best = Math.min(best, search(counts, index, sets + 1, partials, pairs));
    give(0, 1);
    give(1, 1);
    give(2, 1);
  }

  if (blocksUsed < 5) {
    if (at(counts, index) >= 2) {
      take(0, 2);
      best = Math.min(best, search(counts, index, sets, partials, pairs + 1));
      give(0, 2);
    }
    for (const gap of [1, 2]) {
      if (!canReachAtIndex(index, gap)) continue;
      if (at(counts, index + gap) === 0) continue;
      take(0, 1);
      take(gap, 1);
      best = Math.min(best, search(counts, index, sets, partials + 1, pairs));
      give(0, 1);
      give(gap, 1);
    }
  }

  // Or leave this tile floating.
  take(0, 1);
  best = Math.min(best, search(counts, index, sets, partials, pairs));
  give(0, 1);

  return best;
}

const cache = new Map<string, number>();

/** `declaredMelds` counts exposed and concealed melds; each is a finished set. */
export function shanten(kinds: readonly TileKind[], declaredMelds = 0): number {
  const counts = countKinds(kinds);
  const key = `${declaredMelds}|${counts.join("")}`;
  const cached = cache.get(key);
  if (cached !== undefined) return cached;

  const value = search(counts, 0, declaredMelds, 0, 0);
  if (cache.size < 200_000) cache.set(key, value);
  return value;
}

/** Tiles that would lower the shanten of this hand if drawn. */
export function usefulTiles(kinds: readonly TileKind[], declaredMelds = 0): TileKind[] {
  const current = shanten(kinds, declaredMelds);
  const counts = countKinds(kinds);
  const useful: TileKind[] = [];
  for (let index = 0; index < KIND_COUNT; index += 1) {
    if (at(counts, index) >= 4) continue;
    const kind = kindAtIndex(index);
    if (shanten([...kinds, kind], declaredMelds) < current) useful.push(kind);
  }
  return useful;
}

export function isHonor(kind: TileKind): boolean {
  return TILE_INFO[kind].isHonor;
}
